import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { Card, HierarchyTree, Loader } from '../index';
import { colors } from '../../styles/colors';
import Environments from '../../assets/img/icons/environment.svg';
import Active from '../../assets/img/icons/active.svg';
import Machines from '../../assets/img/icons/machines.svg';
import Energy from '../../assets/img/icons/energy.svg';
import Water from '../../assets/img/icons/water.svg';
import Lighting from '../../assets/img/icons/lighting.svg';
import Nobreaks from '../../assets/img/icons/nobreak.svg';
import Utilities from '../../assets/img/icons/utilities.svg';

import {
  ItemTitle, ItemSubTitle, ItemValue, ItemVal, Overlay, NewCardPersonalized, ServerStyledBorder, TooltipContainer, BoxHover, ContainerIconImage,
  ButtonRedirect,
} from './styles';
import { BsSquareFill } from 'react-icons/bs';
import { getUserProfile } from '../../helpers/userProfile';
import ReactTooltip from 'react-tooltip';
import { CSSProperties, ReactElement, useState } from 'react';
import { thousandPointFormat } from '~/helpers/thousandFormatNumber';
import { UnitStatus } from 'providers/types/unit';

type ConnectedDevicesProps = {
  devicesCount: {
    onlineDevs: number,
    offlineDevs: number,
    modernization?: number
  } | undefined;
  unitsCount: Record<UnitStatus, number>;
  maxWidth?: number;
  marginLeft: number;
  marginRight: number;
  isLoading: boolean;
  utils: {
    offlineCountByReason: {
      reason: string;
      count: number;
    }[],
    onlineStatusByApplication: {
      application: string;
      online: number;
      offline: number;
    }[],
    maquinas: {
      catalogadas: number;
      monitoradas: {
        online: number;
        offline: number;
      }
      modernization?: number;
    },
    ambientes: {
      online: number;
      offline: number;
      unMonitored: number;
      modernization?: number;
    },
    energia: {
      online: number;
      offline: number;
      modernization?: number;
    },
    agua: {
      online: number;
      offline: number;
      modernization?: number;
    },
    iluminacao: {
      online: number;
      offline: number;
    },
    nobreak: {
      online: number;
      offline: number;
    },
    asset: {
      total: number;
      online: number;
      offline: number;
    }
  },
}

function ItemCount({
  color,
  count,
  text,
  style,
}: {
  count: number | string;
  text: string;
  color: string;
  style?: CSSProperties;
}): ReactElement {
  return (
    <Flex flexDirection="row" alignItems="baseline" style={{ height: '15px', ...style }}>
      <BsSquareFill
        size={15}
        color={color}
        className="margin-right"
        style={{ marginTop: 'auto' }}
      />
      <ItemTitle>
        {text}
        :
      </ItemTitle>
      <ItemSubTitle className="margin-left">
        {typeof count === 'number' ? thousandPointFormat(count) : count}
      </ItemSubTitle>
    </Flex>
  );
}

export const ConnectedDevices = ({
  devicesCount,
  unitsCount,
  utils,
  isLoading = false,
}: ConnectedDevicesProps): JSX.Element => {
  const { t } = useTranslation();
  const [profile] = useState(getUserProfile);
  const utilitaries = utils?.nobreak?.offline + utils?.nobreak?.online + utils?.iluminacao?.offline + utils?.iluminacao?.online;
  const isDesktop = window.matchMedia('(min-width: 426px)');
  const isMobile = !isDesktop.matches;

  const totalUnitCount = unitsCount[UnitStatus.INSTALLATION]
  + unitsCount[UnitStatus.OPERATION]
  + unitsCount[UnitStatus.MODERNIZATION];

  const totalDevices = devicesCount ? (devicesCount.onlineDevs + devicesCount.offlineDevs + (devicesCount.modernization ?? 0)) : 0;

  const totalEnvironments = utils?.ambientes ? utils.ambientes.online + utils.ambientes.offline + utils.ambientes.unMonitored + (utils.ambientes.modernization ?? 0) : 0;

  const totalEnergy = utils?.energia ? utils.energia.online + utils.energia.offline + (utils.energia.modernization ?? 0) : 0;

  const totalWater = utils?.agua ? (utils.agua.offline + utils.agua.online
  + (utils.agua.modernization ?? 0)) : 0;

  const hasModernizationUnits = unitsCount[UnitStatus.MODERNIZATION] > 0;

  function renderTooltipDevices() {
    return (
      <ReactTooltip
        id="tooltip-dispositivos"
        place="top"
        textColor="#000000"
        backgroundColor="#FFFFFF"
        className="boxShadow"
      >
        <TooltipContainer>
          <Flex flexWrap="wrap" justifyContent="center" alignItems="center" flexDirection="column">
            <Box justifyContent="start">
              <ItemTitle>
                {t('dispositivos')}
              </ItemTitle>
              <ItemCount
                color={colors.BlueSecondary}
                count={devicesCount?.onlineDevs ?? 0}
                text={t('online')}
                style={{
                  marginTop: '4px',
                }}
              />
              {hasModernizationUnits && (
              <ItemCount
                color={colors.BlueSecondary}
                count={devicesCount?.modernization ?? 0}
                text={t('emModernizacao')}
                style={{
                  marginTop: '1px',
                }}
              />
              )}
              <ItemCount
                color={colors.Grey300}
                count={devicesCount?.offlineDevs ?? 0}
                text={t('offline')}
                style={{
                  marginTop: '1px',
                }}
              />
            </Box>
          </Flex>
        </TooltipContainer>
      </ReactTooltip>
    );
  }

  function renderTooltipDevicesBb(typeTooltip: string, isPercentage = false) {
    const totalDevsOn = utils.onlineStatusByApplication.reduce((acc, item) => acc + item.online, 0);
    const totalDevsOff = utils.offlineCountByReason.reduce((acc, item) => acc + item.count, 0);
    const totalDevs = totalDevsOff + totalDevsOn;

    const getDisplayValue = (count: number) => {
      if (!isPercentage || totalDevs === 0) return count;
      return `${((count / totalDevs) * 100).toFixed(1)}%`;
    };

    return (
      <ReactTooltip
        id={`tooltip-${typeTooltip}`}
        place="top"
        textColor="#000000"
        backgroundColor="#FFFFFF"
        className="boxShadow"
      >
        <TooltipContainer>
          <Flex flexWrap="wrap" justifyContent="center" alignItems="center" flexDirection="column">
            <Box justifyContent="start">
              <ItemTitle>{t(`${typeTooltip}`)}</ItemTitle>

              <ItemCount
                color={colors.BlueSecondary}
                count={getDisplayValue(totalDevsOn)}
                text={t('online')}
                style={{ marginTop: '4px' }}
              />

              <HierarchyTree
                parent={(
                  <ItemCount
                    color={colors.Grey300}
                    count={getDisplayValue(totalDevsOff)}
                    text={t('offline')}
                    style={{ marginTop: '3px' }}
                  />
              )}
                childrenNodes={
                  utils.offlineCountByReason.map((item) => (
                    <ItemCount
                      key={item.reason}
                      color={colors.Grey300}
                      count={getDisplayValue(item.count ?? 0)}
                      text={item.reason}
                      style={{ marginTop: '1px' }}
                    />
                  ))
                }
              />
            </Box>
          </Flex>
        </TooltipContainer>
      </ReactTooltip>
    );
  }

  function calculateTotal(type: 'percent' | 'total') {
    const totalDevsOn = utils.onlineStatusByApplication.reduce((acc, item) => acc + item.online, 0);
    const totalDevsOff = utils.offlineCountByReason.reduce((acc, item) => acc + item.count, 0);
    const totalDevices = totalDevsOff + totalDevsOn;

    if (type === 'total') {
      return totalDevsOn;
    }

    if (type === 'percent') {
      return totalDevices > 0
        ? `${((totalDevsOn / totalDevices) * 100).toFixed(2).replace('.', ',')}%`
        : '0%';
    }

    return 0;
  }

  return (
    <NewCardPersonalized>
      <Card>

        {isLoading ? (
          <Overlay>
            <Loader variant="primary" size="large" />
          </Overlay>
        ) : (<> </>)}

        <Flex
          className="wrapFlexContainer"
          flexWrap="wrap"
          justifyContent={isMobile ? 'space-between' : 'center'}
          style={{ gap: isMobile ? '15px' : '35px' }}
        >
          <Flex flexWrap="wrap" flexDirection="column" alignItems="flex-start">
            <Box>
              <ItemValue>
                <ItemVal>
                  {thousandPointFormat(totalUnitCount) || '-'}
                </ItemVal>
              </ItemValue>
            </Box>
            <Box>
              <ItemTitle>
                {t('unidades')}
              </ItemTitle>
            </Box>
          </Flex>

          {(profile.manageAllClients || hasModernizationUnits) && (
            <Flex className="wrapFlexContainer disableBorder" flexWrap="nowrap" style={{ minWidth: '110px' }}>
              <Flex flexWrap="wrap" flexDirection="column" alignItems="flex-start">
                <Box>
                  <ItemValue>
                    <ItemVal style={{
                      fontSize: '11px',
                      marginTop: '7px',
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                    >
                      {t('emOperacao')}
                      <span style={{
                        fontSize: '9px', marginLeft: '4px', fontWeight: 'normal', marginTop: '2px',
                      }}
                      >
                        {thousandPointFormat(unitsCount[UnitStatus.OPERATION])}
                      </span>
                    </ItemVal>
                  </ItemValue>
                </Box>
                {profile.manageAllClients && (
                  <Box>
                    <ItemValue>
                      <ItemVal style={{
                        fontSize: '11px',
                        marginTop: '8px',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                      >
                        {t('emInstalacao')}
                        <span style={{
                          fontSize: '9px', marginLeft: '4px', fontWeight: 'normal', marginTop: '2px',
                        }}
                        >
                          {thousandPointFormat(unitsCount[UnitStatus.INSTALLATION])}
                        </span>
                      </ItemVal>
                    </ItemValue>
                  </Box>
                )}
                <Box>
                  <ItemValue>
                    <ItemVal style={{
                      fontSize: '11px',
                      marginTop: '8px',
                      display: 'flex',
                      flexDirection: 'row',
                    }}
                    >
                      {t('emModernizacao')}
                      <span style={{
                        fontSize: '9px', marginLeft: '4px', fontWeight: 'normal', marginTop: '2px',
                      }}
                      >
                        {thousandPointFormat(unitsCount[UnitStatus.MODERNIZATION])}
                      </span>
                    </ItemVal>
                  </ItemValue>
                </Box>
              </Flex>
            </Flex>
          )}
          {!profile.permissions.PER_CLIENT.some((c) => c.clientId === 145) ? renderTooltipDevices() : renderTooltipDevicesBb('dispositivos', false)}
          <Flex className="wrapFlexContainer" flexWrap="nowrap" justifyContent="space-between">
            <BoxHover data-tip data-for="tooltip-dispositivos">
              <Flex flexWrap="wrap" flexDirection="column" alignItems="flex-start">
                <Box>
                  <ItemValue>
                    <ItemVal>
                      {thousandPointFormat(totalDevices) ?? '-'}
                    </ItemVal>
                  </ItemValue>
                  <ItemTitle>
                    {t('dispositivos')}
                  </ItemTitle>
                </Box>
              </Flex>
            </BoxHover>
          </Flex>

          {
            profile.permissions.PER_CLIENT.some((c) => c.clientId === 145) && (
              <>
                {renderTooltipDevicesBb('conectividade', true)}
                <Flex className="wrapFlexContainer" flexWrap="nowrap" justifyContent="space-between" flexDirection="column">
                  <BoxHover data-tip data-for="tooltip-conectividade">
                    <Flex flexWrap="wrap" flexDirection="column" alignItems="flex-start">
                      <Box>
                        <ItemValue>
                          <ItemVal>
                            {
                              calculateTotal('percent')
                          }
                          </ItemVal>
                        </ItemValue>
                        <ItemTitle>
                          {t('conectividade')}
                        </ItemTitle>
                      </Box>
                    </Flex>
                  </BoxHover>
                  <ButtonRedirect to="/analise/conectividade">
                    {t('analiseDetalhada')}
                  </ButtonRedirect>
                </Flex>
              </>
            )
          }

          {!!profile.manageAllClients && (
            <>
              <ReactTooltip
                id="tooltip-ativo"
                place="top"
                textColor="#000000"
                backgroundColor="#FFFFFF"
                className="boxShadow"
              >
                <TooltipContainer>
                  <strong>{t('ativos')}</strong>
                  <Flex mt={1}>
                    <span>
                      {t('monitorados')}
                      :
                    </span>
                    <ItemSubTitle className="margin-left">
                      {thousandPointFormat(utils && utils.asset.offline + utils.asset.online)}
                    </ItemSubTitle>
                  </Flex>
                  <Flex flexDirection="row" mt={1}>
                    <BsSquareFill size={15} color={colors.BlueSecondary} className="margin-right" />
                    <span>
                      {t('online')}
                      :
                    </span>
                    <ItemSubTitle className="margin-left">
                      {thousandPointFormat(utils && utils.asset.online)}
                    </ItemSubTitle>
                  </Flex>
                  <Flex flexDirection="row">
                    <BsSquareFill size={15} color={colors.Grey300} className="margin-right" />
                    <span>
                      {t('offline')}
                      :
                    </span>
                    <ItemSubTitle className="margin-left">
                      {thousandPointFormat(utils && utils.asset.offline)}
                    </ItemSubTitle>
                  </Flex>
                </TooltipContainer>
              </ReactTooltip>
              <Flex flexDirection="row" style={{ minWidth: '110px' }}>
                <BoxHover data-tip data-for="tooltip-ativo">
                  <ServerStyledBorder>
                    <img src={Active} alt="ativo" />
                  </ServerStyledBorder>
                  <Flex flexDirection="column" justifyContent="space-between">
                    <Box>
                      <ItemValue>
                        <ItemVal>
                          {thousandPointFormat(utils && utils.asset.total)}
                        </ItemVal>
                      </ItemValue>
                      <ItemTitle>
                        {t('ativos')}
                      </ItemTitle>
                    </Box>
                  </Flex>
                </BoxHover>
              </Flex>
            </>
          )}

          <ReactTooltip
            id="tooltip-maquinas"
            place="top"
            textColor="#000000"
            backgroundColor="#FFFFFF"
            className="boxShadow"
          >
            <TooltipContainer>
              <strong>{t('maquinas')}</strong>
              <Flex mt={1}>
                <span>
                  {t('monitoradas')}
                  :
                </span>
                <ItemSubTitle className="margin-left">
                  {thousandPointFormat(utils?.maquinas && utils.maquinas.monitoradas.online + utils.maquinas.monitoradas.offline)}
                </ItemSubTitle>
              </Flex>
              <ItemCount
                color={colors.BlueSecondary}
                count={utils?.maquinas?.monitoradas.online ?? 0}
                text={t('online')}
                style={{
                  marginTop: '4px',
                }}
              />
              <ItemCount
                color={colors.Grey300}
                count={utils?.maquinas?.monitoradas.offline ?? 0}
                text={t('offline')}
                style={{
                  marginTop: '1px',
                }}
              />
              {hasModernizationUnits && (
                <ItemCount
                  color={colors.Grey400}
                  count={utils?.maquinas?.modernization ?? 0}
                  text={t('emModernizacao')}
                  style={{
                    marginTop: '1px',
                  }}
                />
              )}
            </TooltipContainer>
          </ReactTooltip>
          <Flex flexDirection="row" style={{ minWidth: '110px' }}>
            <BoxHover data-tip data-for="tooltip-maquinas">
              <ServerStyledBorder>
                <img src={Machines} alt="máquinas" />
              </ServerStyledBorder>
              <Flex flexDirection="column" justifyContent="space-between">
                <Box>
                  <ItemValue>
                    <ItemVal>
                      {thousandPointFormat(utils?.maquinas?.catalogadas)}
                    </ItemVal>
                  </ItemValue>
                  <ItemTitle>
                    {t('maquinas')}
                  </ItemTitle>
                </Box>
              </Flex>
            </BoxHover>
          </Flex>

          <ReactTooltip
            id="tooltip-ambientes"
            place="top"
            textColor="#000000"
            backgroundColor="#FFFFFF"
            className="boxShadow"
          >
            <TooltipContainer>
              <strong>{t('ambientes')}</strong>
              <ItemCount
                color={colors.BlueSecondary}
                count={utils?.ambientes?.online ?? 0}
                text={t('online')}
                style={{
                  marginTop: '4px',
                }}
              />
              <ItemCount
                color={colors.Grey300}
                count={utils?.ambientes?.offline ?? 0}
                text={t('offline')}
                style={{
                  marginTop: '1px',
                }}
              />
              {hasModernizationUnits && (
                <ItemCount
                  color={colors.Grey400}
                  count={utils?.ambientes?.modernization ?? 0}
                  text={t('emModernizacao')}
                  style={{
                    marginTop: '1px',
                  }}
                />
              )}
            </TooltipContainer>
          </ReactTooltip>
          <Flex flexDirection="row" style={{ minWidth: '110px' }}>
            <BoxHover data-tip data-for="tooltip-ambientes">
              <ServerStyledBorder>
                <img src={Environments} alt="" />
              </ServerStyledBorder>
              <Flex flexDirection="column" justifyContent="space-between">
                <Box>
                  <ItemValue>
                    <ItemVal>
                      {thousandPointFormat(totalEnvironments)}
                    </ItemVal>
                  </ItemValue>
                  <ItemTitle>
                    {t('ambientes')}
                  </ItemTitle>
                </Box>
              </Flex>
            </BoxHover>
          </Flex>

          <ReactTooltip
            id="tooltip-energia"
            place="top"
            textColor="#000000"
            backgroundColor="#FFFFFF"
            className="boxShadow"
          >
            <TooltipContainer>
              <strong>{t('energia')}</strong>
              <span>{t('medidores')}</span>
              <ItemCount
                color={colors.BlueSecondary}
                count={utils?.energia?.online ?? 0}
                text={t('online')}
                style={{
                  marginTop: '4px',
                }}
              />
              <ItemCount
                color={colors.Grey300}
                count={utils?.energia?.offline ?? 0}
                text={t('offline')}
                style={{
                  marginTop: '1px',
                }}
              />
              {hasModernizationUnits && (
              <ItemCount
                color={colors.Grey400}
                count={utils?.energia?.modernization ?? 0}
                text={t('emModernizacao')}
                style={{
                  marginTop: '1px',
                }}
              />
              )}
            </TooltipContainer>
          </ReactTooltip>
          <Flex flexDirection="row" style={{ minWidth: '110px' }}>
            <BoxHover data-tip data-for="tooltip-energia">
              <ServerStyledBorder>
                <img src={Energy} alt="" />
              </ServerStyledBorder>
              <Flex flexDirection="column" justifyContent="space-between">
                <Box>
                  <ItemValue>
                    <ItemVal>
                      {thousandPointFormat(totalEnergy)}
                    </ItemVal>
                  </ItemValue>
                  <Box>
                    <ItemTitle>
                      {t('energia')}
                    </ItemTitle>
                    <ItemSubTitle>
                      {t('medidores')}
                    </ItemSubTitle>
                  </Box>
                </Box>
              </Flex>
            </BoxHover>
          </Flex>

          {utils?.agua && totalWater > 0 && (
          <>
            <ReactTooltip
              id="tooltip-agua"
              place="top"
              textColor="#000000"
              backgroundColor="#FFFFFF"
              className="boxShadow"
            >
              <TooltipContainer>
                <strong>{t('agua')}</strong>
                <span>{t('medidores')}</span>
                <ItemCount
                  color={colors.BlueSecondary}
                  count={utils.agua?.online ?? 0}
                  text={t('online')}
                  style={{
                    marginTop: '4px',
                  }}
                />
                <ItemCount
                  color={colors.Grey300}
                  count={utils.agua?.offline ?? 0}
                  text={t('offline')}
                  style={{
                    marginTop: '1px',
                  }}
                />
                {hasModernizationUnits && (
                  <ItemCount
                    color={colors.Grey400}
                    count={utils.agua?.modernization ?? 0}
                    text={t('emModernizacao')}
                    style={{
                      marginTop: '1px',
                    }}
                  />
                )}
              </TooltipContainer>
            </ReactTooltip>
            <Flex flexDirection="row" style={{ minWidth: '110px' }}>
              <BoxHover data-tip data-for="tooltip-agua">
                <ServerStyledBorder>
                  <img src={Water} alt="" />
                </ServerStyledBorder>
                <Flex flexDirection="column" justifyContent="space-between">
                  <Box>
                    <ItemValue>
                      <ItemVal>
                        {thousandPointFormat(totalWater)}
                      </ItemVal>
                    </ItemValue>
                    <Box>
                      <ItemTitle>
                        {t('agua')}
                      </ItemTitle>
                      <ItemSubTitle>
                        {t('medidores')}
                      </ItemSubTitle>
                    </Box>
                  </Box>
                </Flex>
              </BoxHover>
            </Flex>

          </>
          )}

          {
            utilitaries > 0 && (
              <>
                <ReactTooltip
                  id="tooltip-utilitarios"
                  place="top"
                  textColor="#000000"
                  backgroundColor="#FFFFFF"
                  className="boxShadow"
                >
                  <TooltipContainer>
                    <Flex flexDirection="column">
                      <ContainerIconImage>
                        <img src={Lighting} alt="" />
                        <strong>
                          {t('iluminacao')}
                          :
                        </strong>
                        <span>
                          {thousandPointFormat(utils?.iluminacao && utils.iluminacao.online + utils.iluminacao.offline)}
                        </span>
                      </ContainerIconImage>
                      <Flex flexDirection="row" marginTop={1}>
                        <BsSquareFill size={15} color={colors.BlueSecondary} className="margin-right" />
                        <span>
                          {t('online')}
                          :
                        </span>
                        <ItemSubTitle className="margin-left">
                          {thousandPointFormat(utils?.iluminacao?.online)}
                        </ItemSubTitle>
                      </Flex>
                      <Flex flexDirection="row">
                        <BsSquareFill size={15} color={colors.Grey300} className="margin-right" />
                        <span>
                          {t('offline')}
                          :
                        </span>
                        <ItemSubTitle className="margin-left">
                          {thousandPointFormat(utils?.iluminacao?.offline)}
                        </ItemSubTitle>
                      </Flex>
                    </Flex>

                    <Flex flexDirection="column" marginTop={2}>
                      <ContainerIconImage>
                        <img src={Nobreaks} alt="" />
                        <strong>
                          {t('nobreak')}
                          :
                        </strong>
                        <span>
                          {thousandPointFormat(utils?.nobreak && utils.nobreak.online + utils.nobreak.offline)}
                        </span>
                      </ContainerIconImage>
                      <Flex flexDirection="row" marginTop={1}>
                        <BsSquareFill size={15} color={colors.BlueSecondary} className="margin-right" />
                        <span>
                          {t('online')}
                          :
                        </span>
                        <ItemSubTitle className="margin-left">
                          {thousandPointFormat(utils?.nobreak?.online)}
                        </ItemSubTitle>
                      </Flex>
                      <Flex flexDirection="row">
                        <BsSquareFill size={15} color={colors.Grey300} className="margin-right" />
                        <span>
                          {t('offline')}
                          :
                        </span>
                        <ItemSubTitle className="margin-left">
                          {thousandPointFormat(utils?.nobreak?.offline)}
                        </ItemSubTitle>
                      </Flex>
                    </Flex>
                  </TooltipContainer>
                </ReactTooltip>
                <Flex flexDirection="row">
                  <BoxHover data-tip data-for="tooltip-utilitarios">
                    <ServerStyledBorder>
                      <img src={Utilities} alt="" />
                    </ServerStyledBorder>
                    <Flex flexDirection="column" justifyContent="space-between">
                      <Box>
                        <ItemValue>
                          <ItemVal>
                            {thousandPointFormat(utils?.iluminacao && utils?.nobreak && utils.iluminacao.online + utils.iluminacao.offline + utils.nobreak.online + utils.nobreak.offline)}
                          </ItemVal>
                        </ItemValue>
                        <Box>
                          <ItemTitle>
                            {t('utilitarios')}
                          </ItemTitle>
                        </Box>
                      </Box>
                    </Flex>
                  </BoxHover>
                </Flex>
              </>
            )
          }
        </Flex>
      </Card>
    </NewCardPersonalized>
  );
};
